import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import dayjs from 'dayjs';

import { PeriodDatesValidation } from './report.service.model';

@Injectable({
    providedIn: 'root',
})
export class ReportService {
    constructor(private readonly fb: UntypedFormBuilder) {
        this.initForm();
    }

    reportForm: UntypedFormGroup;

    /**
     * Create an empty report form
     */
    initForm() {
        this.reportForm = this.fb.group({});
    }

    /**
     * Dynamically add a new formgroup to the report form.
     * @param {string} name : name of the new formgroup
     * @param {FormGroup} newFormGroup : new formgroup object
     * @returns {void}
     */
    addGroupToReportForm(name: string, newFormGroup: UntypedFormGroup): void {
        this.reportForm.addControl(name, newFormGroup);
    }

    /**
     * Set new group values on an existing group
     * @param group : group name
     * @param values : new values for group
     * @returns {void}
     */

    setReportValue(group: string, values: any): void {
        this.reportForm.get(group).patchValue(values);
    }

    resetForm(): void {
        this.reportForm = new UntypedFormGroup({});
    }

    /**
     * Custom validation to check if start date is before end date
     * @param group : group which holds the start date and end date
     * @returns {PeriodDatesValidation} : result of period dates validation
     */
    validatePeriodDates(group: AbstractControl): PeriodDatesValidation {
        const startDate = group.get('startDate').value as dayjs.Dayjs;
        const endDate = group.get('endDate').value as dayjs.Dayjs;
        const startDateDirtyOrTouched = group.get('startDate').dirty || group.get('startDate').touched;
        const endDateDirtyOrTouched = group.get('endDate').dirty || group.get('endDate').touched;

        if (
            startDate &&
            startDate.isValid() &&
            endDate &&
            endDate.isValid() &&
            startDateDirtyOrTouched &&
            endDateDirtyOrTouched
        ) {
            if (startDate.isAfter(endDate, 'day')) {
                return { startDateAfterEndDate: true };
            } else if (endDate.diff(startDate, 'day') + 1 > 100) {
                // use +1 to make it inclusive
                return { maxNumberOfDaysReached: true };
            }
        }
        return null;
    }

    /**
     * Check if a report is a daily report based on from- and toDate
     * @param {Date} fromDate : report fromDate
     * @param {Date} toDate : report toDate
     * @returns {boolean} : result of period dates validation
     */
    isDailyReport(fromDate: Date, toDate: Date): boolean {
        if (!toDate && !fromDate) return;

        const reportFromDate = new Date(fromDate);
        const reportToDate = new Date(toDate);

        return (
            reportToDate.getFullYear() === reportFromDate.getFullYear() &&
            reportToDate.getMonth() === reportFromDate.getMonth() &&
            reportToDate.getDate() === reportFromDate.getDate()
        );
    }

    markControlsDirty(group: UntypedFormGroup | UntypedFormArray): void {
        Object.keys(group.controls).forEach((key: string) => {
            const abstractControl = group.get(key);

            if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
                this.markControlsDirty(abstractControl);
            } else {
                abstractControl.markAsDirty();
            }
        });
    }
}
